import { ConnectedPosition } from "@angular/cdk/overlay";
import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { format } from "date-fns";
import { firstValueFrom } from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationUserService } from "@bitwarden/common/abstractions/organization-user/organization-user.service";
import {
  OrganizationUserConfirmRequest,
  OrganizationUserInviteRequest,
  OrganizationUserUpdateRequest,
} from "@bitwarden/common/abstractions/organization-user/requests";
import { OrganizationUserUserDetailsResponse } from "@bitwarden/common/abstractions/organization-user/responses";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { SyncService } from "@bitwarden/common/abstractions/sync/sync.service.abstraction";
import { ValidationService } from "@bitwarden/common/abstractions/validation.service";
import { OrganizationUserStatusType } from "@bitwarden/common/enums/organizationUserStatusType";
import { OrganizationUserType } from "@bitwarden/common/enums/organizationUserType";
import { CollectionData } from "@bitwarden/common/models/data/collection.data";
import { Organization } from "@bitwarden/common/models/domain/organization";
import { Utils } from "@bitwarden/common/misc/utils";
import { CollectionRequest } from "@bitwarden/common/models/request/collection.request";
import { SelectionReadOnlyRequest } from "@bitwarden/common/models/request/selection-read-only.request";
import { CollectionDetailsResponse } from "@bitwarden/common/models/response/collection.response";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { ConfirmationDialogComponent } from "@bitwarden/web-vault/app/components/confirmation-dialog/confirmation-dialog.component";
import { mobileBottomSheetConfig } from "@bitwarden/web-vault/app/components/dialog/mobile-bottom-sheet-dialog";
import {
  TEAM_MEMBER_DECLINED_BADGE_CLASS,
  TEAM_STATUS_BADGE_CLASS,
  TEAM_STATUS_LABEL_KEY,
  TeamStatusKey,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-status";
import { formatRelativeTime } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/relative-time";
import { getAccountsInTeam } from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-accounts";
import {
  DeclinedTeamInviteResponse,
  PendingTeamInviteResponse,
  TeamInvitationService,
} from "@bitwarden/web-vault/app/gloss/manage/manage-organizations/team-invitation.service";
import { StoreLoaderService } from "@bitwarden/web-vault/app/services/dali/store.loader.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

interface MenuItem {
  label: string;
  danger?: boolean;
  onClick: () => void;
}

interface MemberRow {
  id: string;
  name: string;
  email: string;
  isOwner: boolean;
  subtitle: string;
  badgeKey?: TeamStatusKey;
  // Badge shown next to actionButton (rows with an action show this instead of badgeKey):
  // "Accepted" beside Confirm — the Owner/Awaiting look with its own text, so it doesn't touch
  // the shared statusAwaiting label used elsewhere (e.g. the Invitations tab) — and "Declined"
  // beside Invite again.
  actionBadge?: { labelKey: string; cssClass: string };
  // Typography for actionButton's label (app-gloss-button applies it to the inner span, which
  // otherwise forces font-medium).
  actionButtonFontClass?: string;
  actionButton?: ActionButton;
  menuItems: MenuItem[];
  menuOpen: boolean;
  isBusy: boolean;
  // Confirmed members (incl. the owner). Invited, Accepted-but-not-confirmed and declined rows
  // are listed but left out of the "Members · N" count.
  countsAsMember?: boolean;
}

// Hosted inside the "select a team" right-panel drawer opened from OrganizationPageComponent.
// Only owners get the management UI (members/shared accounts/team settings) — everyone else
// sees a read-only note, matching the pre-existing org.isOwner gate.
@Component({
  selector: "app-team-detail",
  templateUrl: "./team-detail.component.html",
})
export class TeamDetailComponent implements OnChanges, OnDestroy {
  private readonly apiService = inject(ApiService);
  private readonly collectionService = inject(CollectionService);
  private readonly cryptoService = inject(CryptoService);
  private readonly organizationUserService = inject(OrganizationUserService);
  private readonly platformUtilsService = inject(PlatformUtilsService);
  private readonly validationService = inject(ValidationService);
  private readonly i18nService = inject(I18nService);
  private readonly logService = inject(LogService);
  private readonly dialog = inject(MatDialog);
  private readonly userStore = inject(UserStoreService);
  private readonly cipherService = inject(CipherService);
  private readonly syncService = inject(SyncService);
  private readonly storeLoader = inject(StoreLoaderService);
  private readonly teamInvitationService = inject(TeamInvitationService);

  @Input() org!: Organization;
  @Input() collectionId!: string;
  @Input() collectionName = "";
  // Dialog-opened components can't rely on @Output — DialogWrapperComponent renders this
  // component via *ngComponentOutlet, which only wires up @Input()s (see CreateCollectionComponent
  // for the same callback-based pattern).
  @Input() onRenamed?: (name: string) => void;
  @Input() onDeleted?: () => void;
  // Lets the "My teams" list row (member count, shared count) catch up immediately instead of
  // only refreshing once the drawer closes.
  @Input() onMembersChanged?: () => void;
  // Closes this drawer and deep-links to the accounts list with ?accountId=... — the same
  // query-param contract SearchBarComponent uses, which ManageAccountsComponent already reads
  // to auto-select the matching row.
  @Input() onNavigateToAccounts?: (accountId: string) => void;

  readonly badgeClass = TEAM_STATUS_BADGE_CLASS;
  readonly badgeLabelKey = TEAM_STATUS_LABEL_KEY;
  readonly menuPositions: ConnectedPosition[] = [
    { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top", offsetY: 4 },
    { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom", offsetY: -4 },
  ];

  memberRows: MemberRow[] = [];
  isLoading = false;
  hasLoadError = false;

  addingMember = false;
  newMemberEmail = "";
  addMemberError = false;
  isInviting = false;

  sharedAccounts: { id: string; name: string }[] = [];

  teamName = "";
  isRenaming = false;

  deleteTeamButton = new ActionButton({
    fullClass:
      "tw-w-auto tw-h-[36px] tw-px-4 tw-whitespace-nowrap tw-rounded-[8px] border-gradient-neuomorphic tw-bg-color-surface-page tw-shadow-elevation-card !tw-shadow-accounts-detail-surface hover:!tw-shadow-accounts-detail-surface active:!tw-shadow-accounts-detail-surface tw-text-color-status-error-text",
    text: this.i18nService.t("deleteTeam"),
    onClick: () => this.confirmDeleteTeam(),
  });

  private originalTeamName = "";
  private isDeleting = false;
  private confirming = new Set<string>();
  // Bumped per loadMembers() call, so a slower, older response can't overwrite a newer one when
  // several row actions reload the list back to back.
  private loadSeq = 0;
  private openDialogRef: MatDialogRef<ConfirmationDialogComponent> | null = null;
  private destroyed = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes["collectionName"] && this.collectionName) {
      this.teamName = this.collectionName;
      this.originalTeamName = this.collectionName;
    }
    if (changes["org"] && this.org) {
      void this.loadSharedAccounts();
      if (this.org.isOwner) {
        void this.loadMembers();
      }
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.openDialogRef?.close(false);
  }

  retryLoadMembers() {
    void this.loadMembers();
  }

  goToAccounts(accountId: string) {
    this.onNavigateToAccounts?.(accountId);
  }

  toggleAddMember() {
    this.addingMember = !this.addingMember;
    this.newMemberEmail = "";
    this.addMemberError = false;
  }

  async submitAddMember() {
    const email = this.newMemberEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.addMemberError = true;
      return;
    }
    if (this.isInviting) {
      return;
    }
    this.isInviting = true;
    try {
      const toastKey = await this.inviteToTeam(email);
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast("success", "", this.i18nService.t(toastKey));
      this.addingMember = false;
      this.newMemberEmail = "";
      this.addMemberError = false;
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        this.isInviting = false;
      }
    }
  }

  // Invites an email to this team; returns the success toast key. Shared by "+ Add member" and
  // a declined row's "Invite again".
  // A "team" is one collection inside a single org, so the email may already have an
  // OrganizationUser row here (another team, removed down to zero teams, or revoked):
  // - Confirmed: joining one more team still goes through its own invite + accept. The invite
  //   endpoint turns that into a team invite (collection_invitations) and emails it.
  // - Revoked: the backend won't let a revoked member accept, and restoring them would quietly
  //   hand back every team they had before. So the old row is deleted and they get a fresh org
  //   invite for this team only (the same hard delete revokeMember uses).
  // - Still Invited/Accepted at the org level: this team is added straight onto their pending
  //   org invite — accepting that already covers it, and a separate team invite couldn't be
  //   accepted before they've joined anyway.
  private async inviteToTeam(email: string): Promise<string> {
    const orgId = this.org.id;
    const existing = await this.findExistingOrgUser(orgId, email);
    if (
      existing &&
      (existing.status === OrganizationUserStatusType.Invited ||
        existing.status === OrganizationUserStatusType.Accepted)
    ) {
      const details = await this.organizationUserService.getOrganizationUser(orgId, existing.id);
      await this.putMemberTeams(orgId, existing.id, details, [
        ...details.collections.filter((c) => c.id !== this.collectionId),
        { id: this.collectionId, readOnly: true, hidePasswords: false },
      ]);
      return "addedToTeam";
    }
    if (existing?.status === OrganizationUserStatusType.Revoked) {
      await this.organizationUserService.deleteOrganizationUser(orgId, existing.id);
      // From here the old row is gone either way, so the list must be reloaded even if the
      // invite below fails (callers only reload on success).
      try {
        await this.postTeamInvite(orgId, email);
      } catch (e) {
        void this.loadMembers();
        throw e;
      }
      return "invited";
    }
    await this.postTeamInvite(orgId, email);
    return "invited";
  }

  // Invites (or re-invites) an email to this team. The backend turns it into an org invite for
  // someone not in the org yet, or a team invite (collection_invitations) for an existing
  // member; either way it replaces any pending one and re-sends the email.
  private async postTeamInvite(orgId: string, email: string) {
    const request = new OrganizationUserInviteRequest();
    request.emails = [email];
    request.type = OrganizationUserType.User;
    request.accessAll = false;
    request.collections = [new SelectionReadOnlyRequest(this.collectionId, true, false)];
    // Plaintext name for the email and the invitee's list; the server only has it encrypted.
    request.teamName = this.originalTeamName;
    await this.organizationUserService.postOrganizationUserInvite(orgId, request);
  }

  private async findExistingOrgUser(
    orgId: string,
    email: string,
  ): Promise<OrganizationUserUserDetailsResponse | undefined> {
    const response = await this.organizationUserService.getAllUsers(orgId);
    const lowerEmail = email.toLowerCase();
    return (response?.data ?? []).find((u) => u.email?.toLowerCase() === lowerEmail);
  }

  // Rewrites which teams a member is in (read-modify-write on their org-user row), keeping
  // their type and access settings. Used to add or drop just this team without touching the
  // member's other teams.
  private async putMemberTeams(
    orgId: string,
    memberId: string,
    details: { type: OrganizationUserType; accessAll: boolean },
    teams: { id: string; readOnly: boolean; hidePasswords: boolean }[],
  ) {
    const request = new OrganizationUserUpdateRequest();
    request.type = details.type;
    request.accessAll = details.accessAll;
    request.collections = teams.map(
      (c) => new SelectionReadOnlyRequest(c.id, c.readOnly, c.hidePasswords),
    );
    await this.organizationUserService.putOrganizationUser(orgId, memberId, request);
  }

  async onTeamNameBlur() {
    const trimmed = this.teamName.trim();
    if (!trimmed || trimmed === this.originalTeamName) {
      this.teamName = this.originalTeamName;
      return;
    }
    if (this.isRenaming) {
      return;
    }
    this.isRenaming = true;
    const orgId = this.org.id;
    // No early exit once the drawer closes: the usual way to finish a rename is to click away
    // (blur) onto the close button, which destroys this component mid-save. The rename still
    // has to reach the server and local state, and the toasts are app-wide.
    try {
      const orgKey = await this.cryptoService.getOrgKey(orgId);
      if (!orgKey) {
        throw new Error("Missing org key");
      }
      const encName = await this.cryptoService.encrypt(trimmed, orgKey);
      if (!encName.encryptedString) {
        throw new Error("Failed to encrypt collection name");
      }
      const request = new CollectionRequest();
      request.name = encName.encryptedString;
      const collection = await this.apiService.putCollection(orgId, this.collectionId, request);
      // putCollection doesn't trigger a sync, so getAllDecrypted() won't see the rename until
      // we upsert it into local state ourselves (mirrors CreateCollectionComponent).
      await this.collectionService.upsert(
        new CollectionData({ ...collection, readOnly: false } as CollectionDetailsResponse),
      );
      this.originalTeamName = trimmed;
      // The template names the team in a few places; keep them in step with the rename.
      this.collectionName = trimmed;
      this.platformUtilsService.showToast("success", "", this.i18nService.t("teamRenamed"));
      this.onRenamed?.(trimmed);
    } catch (e) {
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.teamName = this.originalTeamName;
      this.validationService.showError(e);
    } finally {
      this.isRenaming = false;
    }
  }

  private confirmDeleteTeam() {
    // The delete button's disabled state doesn't render (app-gloss-button is OnPush and this
    // isn't one of its own clicks), so guard here against a second confirm/delete.
    if (this.isDeleting || this.openDialogRef) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      ...mobileBottomSheetConfig(),
      disableClose: true,
      data: {
        title: "deleteThisTeam",
        type: "warning",
        // Rendered as HTML by the dialog.
        message: this.i18nService.t(
          "deleteTeamConfirmation",
          Utils.escapeHtml(this.originalTeamName),
        ),
        customizedButton: {
          yes: { text: "deleteTeam", class: "tw-bg-danger-600 tw-text-white" },
          no: { text: "cancel", class: "neutral" },
        },
      },
    });
    this.openDialogRef = dialogRef;
    void this.awaitDeleteConfirmation(dialogRef);
  }

  private async awaitDeleteConfirmation(dialogRef: MatDialogRef<ConfirmationDialogComponent>) {
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    this.openDialogRef = null;
    if (confirmed && !this.destroyed) {
      await this.deleteTeam();
    }
  }

  private async deleteTeam() {
    this.isDeleting = true;
    this.deleteTeamButton.enableButton(false);
    try {
      await this.teamInvitationService.deleteTeam(
        this.org.id,
        this.collectionId,
        this.originalTeamName,
      );
      // Carries on even if the drawer was closed meanwhile: the team is gone server-side, so
      // local state must follow or it would stay listed.
      await this.collectionService.delete(this.collectionId);
      this.platformUtilsService.showToast("success", "", this.i18nService.t("teamDeleted"));
      this.onDeleted?.();
      // The backend also deleted the org copies of the accounts shared into this team and
      // removed members who were only here; bring local state in line so neither lingers.
      await this.syncService
        .fullSync(true)
        .then(() => this.storeLoader.reloadFromVault())
        .catch((e) => {
          // The team is gone either way, but the stores may now be empty or stale: say so
          // rather than leaving the user looking at a silently wrong vault.
          this.logService.error(e instanceof Error ? e.message : String(e));
          this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
        });
    } catch (e) {
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      this.isDeleting = false;
      if (!this.destroyed) {
        this.deleteTeamButton.enableButton(true);
      }
    }
  }

  private async loadSharedAccounts() {
    const orgId = this.org.id;
    try {
      const accounts = await getAccountsInTeam(
        this.cipherService,
        this.userStore.accounts.accountViews(),
        orgId,
        this.collectionId,
      );
      if (!this.destroyed && this.org.id === orgId) {
        this.sharedAccounts = accounts.map((a) => ({ id: a.id, name: a.name }));
      }
    } catch (e) {
      this.logService.error(e instanceof Error ? e.message : String(e));
    }
  }

  private async loadMembers() {
    const orgId = this.org.id;
    const seq = ++this.loadSeq;
    this.isLoading = true;
    this.hasLoadError = false;
    try {
      const [allUsersResponse, collectionUsers, pendingTeamInvites, declines] = await Promise.all([
        this.organizationUserService.getAllUsers(orgId),
        this.apiService.getCollectionUsers(orgId, this.collectionId),
        // Not fatal: the rest of the member list is still worth showing without these.
        this.teamInvitationService
          .getTeamPendingInvites(orgId, this.collectionId)
          .catch((e): PendingTeamInviteResponse[] => {
            this.logService.error(e instanceof Error ? e.message : String(e));
            return [];
          }),
        this.teamInvitationService
          .getTeamDeclines(orgId, this.collectionId)
          .catch((e): DeclinedTeamInviteResponse[] => {
            this.logService.error(e instanceof Error ? e.message : String(e));
            return [];
          }),
      ]);
      if (this.destroyed || seq !== this.loadSeq) {
        return;
      }
      const collectionMemberIds = new Set(collectionUsers.map((s) => s.id));
      const users = allUsersResponse?.data ?? [];
      const memberRows = users
        // Scoped to this team whatever the member's status: the org user list carries each
        // member's collections, including still-Invited ones (written at invite time). Owners,
        // Admins and AccessAll members see every team. Revoked members (only possible via other
        // clients: revoking here hard-deletes the row, see revokeMember) have no access and
        // nothing this list could do for them, so they're left out; "+ Add member" still
        // handles inviting them again.
        .filter((u) => u.status !== OrganizationUserStatusType.Revoked)
        .filter(
          (u) =>
            u.type === OrganizationUserType.Owner ||
            u.accessAll ||
            u.type === OrganizationUserType.Admin ||
            collectionMemberIds.has(u.id) ||
            u.collections.some((c) => c.id === this.collectionId),
        )
        .map((u) => this.buildMemberRow(u));
      // Existing org members invited to this team who haven't accepted yet (they get a
      // users_collections row only on accept, so the filter above can't see them).
      const teamInviteRows = pendingTeamInvites.map((invite) => this.buildTeamInviteRow(invite));
      // Someone who declined and has since been invited again already has a row above.
      const listedEmails = new Set(
        [...memberRows, ...teamInviteRows].map((r) => r.email?.toLowerCase()),
      );
      const declinedRows = declines
        .filter((d) => !listedEmails.has(d.email?.toLowerCase()))
        .map((d) => this.buildDeclinedRow(d));
      this.memberRows = [...memberRows, ...teamInviteRows, ...declinedRows].sort((a, b) =>
        a.isOwner === b.isOwner ? 0 : a.isOwner ? -1 : 1,
      );
      this.isLoading = false;
    } catch (e) {
      if (this.destroyed || seq !== this.loadSeq) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.isLoading = false;
      this.hasLoadError = true;
    }
  }

  // Shown as "Members · N".
  get memberCount(): number {
    return this.memberRows.filter((r) => r.countsAsMember).length;
  }

  private buildMemberRow(u: OrganizationUserUserDetailsResponse): MemberRow {
    const isOwner = u.type === OrganizationUserType.Owner;
    const row: MemberRow = {
      id: u.id,
      name: u.name,
      email: u.email,
      isOwner,
      subtitle: "",
      menuItems: [],
      menuOpen: false,
      isBusy: false,
      // loadMembers already scoped the list to this team, so every confirmed row is a member.
      countsAsMember: u.status === OrganizationUserStatusType.Confirmed,
    };

    if (isOwner) {
      row.badgeKey = "owner";
      row.subtitle = this.i18nService.t("ownerSubtitle");
    } else if (u.status === OrganizationUserStatusType.Confirmed) {
      row.badgeKey = "member";
      const hasFullAccess = u.accessAll || u.type === OrganizationUserType.Admin;
      const joined = this.collectionJoinedAt(u);
      row.subtitle = hasFullAccess
        ? this.i18nService.t("fullAccess")
        : this.withTime(
            this.i18nService.t("statusMember"),
            joined && this.i18nService.t("joinedAgo", joined),
          );
      if (!hasFullAccess) {
        row.menuItems = [
          {
            label: this.i18nService.t("remove"),
            danger: true,
            onClick: () => void this.removeMember(row),
          },
        ];
      }
    } else if (u.status === OrganizationUserStatusType.Accepted) {
      row.subtitle = this.withTime(
        this.i18nService.t("awaitingConfirmationSubtitle"),
        formatRelativeTime(u.acceptedAt, this.i18nService),
      );
      row.actionBadge = { labelKey: "statusAccepted", cssClass: TEAM_STATUS_BADGE_CLASS.owner };
      row.actionButton = this.buildConfirmButton(u, row);
      row.menuItems = [
        {
          label: this.i18nService.t("reject"),
          danger: true,
          onClick: () => void this.revokeMember(row, "rejected"),
        },
      ];
    } else {
      row.badgeKey = "invited";
      row.subtitle = this.withTime(
        this.i18nService.t("invitedSubtitle"),
        formatRelativeTime(u.invitedAt, this.i18nService),
      );
      row.menuItems = [
        { label: this.i18nService.t("resendInvite"), onClick: () => void this.resendInvite(row) },
        {
          label: this.i18nService.t("cancelInvite"),
          danger: true,
          onClick: () => void this.revokeMember(row, "revoked"),
        },
      ];
    }

    return row;
  }

  // "Invited by you · just now": the time segment is dropped when it's unknown.
  private withTime(label: string, time: string): string {
    return [label, time].filter(Boolean).join(" · ");
  }

  // Relative time this member joined this team, or "" if not recorded (joined before join
  // times were kept).
  private collectionJoinedAt(u: OrganizationUserUserDetailsResponse): string {
    return formatRelativeTime(u.collectionJoinedAt?.get(this.collectionId), this.i18nService);
  }

  // An invite to this team the invitee declined: "Invite again" sends a fresh one (which also
  // clears this record server-side), Remove just dismisses it.
  private buildDeclinedRow(decline: DeclinedTeamInviteResponse): MemberRow {
    const row: MemberRow = {
      id: `decline:${decline.id}`,
      name: decline.name || decline.email,
      email: decline.email,
      isOwner: false,
      subtitle: this.withTime(
        this.i18nService.t("declinedInvitation"),
        decline.declinedAt ? format(decline.declinedAt, "d MMM yyyy") : "",
      ),
      actionBadge: { labelKey: "statusDeclined", cssClass: TEAM_MEMBER_DECLINED_BADGE_CLASS },
      // Design tokens: font/family/body (Montserrat), 600, 13px, line-height/label,
      // button/secondary/label (#008081 = color-text-action).
      actionButtonFontClass:
        "tw-font-montserrat tw-text-[13px] !tw-font-semibold tw-leading-4 tw-text-color-text-action",
      menuItems: [],
      menuOpen: false,
      isBusy: false,
    };
    // Not a submit-type button: those disable themselves on click, and app-gloss-button is
    // OnPush, so they don't come back (see confirmMember). row.isBusy guards double clicks.
    // Same surface as "+ Add member" (it's the same action), at this row's inline size.
    row.actionButton = new ActionButton({
      text: this.i18nService.t("inviteAgain"),
      // Same drop shadow as the Confirm button beside Accepted rows.
      fullClass:
        "tw-h-8 tw-rounded-2xl tw-bg-color-surface-sunken tw-px-3 tw-shadow-elevation-card",
      ghostShadow: true,
      onClick: () => void this.inviteAgain(row),
    });
    row.menuItems = [
      {
        label: this.i18nService.t("remove"),
        danger: true,
        onClick: () => void this.removeDecline(row, decline.id),
      },
    ];
    return row;
  }

  private async inviteAgain(row: MemberRow) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      const toastKey = await this.inviteToTeam(row.email);
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast("success", "", this.i18nService.t(toastKey));
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  private async removeDecline(row: MemberRow, declineId: string) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      await this.teamInvitationService.removeTeamDecline(this.org.id, this.collectionId, declineId);
      if (this.destroyed) {
        return;
      }
      this.memberRows = this.memberRows.filter((r) => r.id !== row.id);
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  // Same look and actions as an org-level "Invited" row, but backed by the team invite
  // (collection_invitations) rather than an OrganizationUser row.
  private buildTeamInviteRow(invite: PendingTeamInviteResponse): MemberRow {
    const row: MemberRow = {
      id: `team-invite:${invite.userId}`,
      name: invite.name,
      email: invite.email,
      isOwner: false,
      subtitle: this.withTime(
        this.i18nService.t("invitedSubtitle"),
        formatRelativeTime(invite.invitedAt, this.i18nService),
      ),
      badgeKey: "invited",
      menuItems: [],
      menuOpen: false,
      isBusy: false,
    };
    row.menuItems = [
      {
        label: this.i18nService.t("resendInvite"),
        onClick: () => void this.resendTeamInvite(row, invite.userId),
      },
      {
        label: this.i18nService.t("cancelInvite"),
        danger: true,
        onClick: () => void this.cancelTeamInvite(row, invite.userId),
      },
    ];
    return row;
  }

  // Re-sends the pending team invite's email; the new link replaces the old one.
  private async resendTeamInvite(row: MemberRow, userId: string) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      await this.teamInvitationService.resendTeamInvite(this.org.id, this.collectionId, userId);
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast("success", "", this.i18nService.t("inviteResent"));
      // The invite is re-dated, so its "Invited by you · …" time starts again.
      await this.loadMembers();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  private async cancelTeamInvite(row: MemberRow, userId: string) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      await this.teamInvitationService.cancelTeamInvite(this.org.id, this.collectionId, userId);
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast("success", "", this.i18nService.t("inviteCancelled"));
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  private async confirmMember(member: OrganizationUserUserDetailsResponse, row: MemberRow) {
    if (this.confirming.has(member.id)) {
      return;
    }
    if (row.isBusy) {
      // Another action (e.g. Reject) is in flight on this row. The submit-type button already
      // disabled itself on click, so hand back a fresh one (see the finally block below).
      row.actionButton = this.buildConfirmButton(member, row);
      return;
    }
    this.confirming.add(member.id);
    // Also locks the row's menu (Reject), so it can't race the confirm.
    row.isBusy = true;
    try {
      const orgId = this.org.id;
      const orgKey = await this.cryptoService.getOrgKey(orgId);
      if (this.destroyed) {
        return;
      }
      if (!orgKey) {
        this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
        return;
      }

      const publicKeyResponse = await this.organizationUserService.postOrganizationUsersPublicKey(
        orgId,
        [member.id],
      );
      if (this.destroyed) {
        return;
      }
      const keyEntry = publicKeyResponse.data[0];
      if (!keyEntry?.key || keyEntry.id !== member.id) {
        this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
        return;
      }

      const publicKey = Utils.fromB64ToArray(keyEntry.key);

      // Verify the fingerprint phrase before trusting the server-supplied public key —
      // prevents a key-substitution attack by a compromised server.
      const fingerprint = await this.cryptoService.getFingerprint(
        keyEntry.userId,
        publicKey.buffer as ArrayBuffer,
      );
      if (this.destroyed) {
        return;
      }
      const fingerprintDialogRef = this.dialog.open(ConfirmationDialogComponent, {
        ...mobileBottomSheetConfig(),
        disableClose: true,
        data: {
          title: "confirm",
          type: "warning",
          message:
            this.i18nService.t("fingerprintEnsureIntegrityVerify") +
            "<br><br><strong>" +
            Utils.escapeHtml(member.name || member.email) +
            "</strong><br><code>" +
            fingerprint.join("-") +
            "</code>",
          customizedButton: {
            yes: { text: "confirm", class: "primary" },
            no: { text: "cancel", class: "neutral" },
          },
        },
      });
      this.openDialogRef = fingerprintDialogRef;
      const userConfirmed = await firstValueFrom(fingerprintDialogRef.afterClosed());
      this.openDialogRef = null;
      if (this.destroyed || !userConfirmed) {
        return;
      }

      const encryptedKey = await this.cryptoService.rsaEncrypt(
        orgKey.key,
        publicKey.buffer as ArrayBuffer,
      );
      if (this.destroyed) {
        return;
      }

      const request = new OrganizationUserConfirmRequest();
      request.key = encryptedKey.encryptedString!;
      await this.organizationUserService.postOrganizationUserConfirm(orgId, member.id, request);
      if (this.destroyed) {
        return;
      }

      this.platformUtilsService.showToast("success", "", this.i18nService.t("confirmed"));
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      this.confirming.delete(member.id);
      row.isBusy = false;
      // A fresh button rather than enableButton(true): the submit-type button disabled itself
      // on click, and app-gloss-button is OnPush, so re-enabling the same object in place (after
      // a cancelled fingerprint dialog or a failure) never re-renders and leaves it looking
      // disabled with its label gone.
      if (row.actionButton) {
        row.actionButton = this.buildConfirmButton(member, row);
      }
    }
  }

  private buildConfirmButton(member: OrganizationUserUserDetailsResponse, row: MemberRow) {
    return new ActionButton({
      text: this.i18nService.t("confirm"),
      type: "submit",
      // Same visual parameters as the "Create team" button (color/shadow/weight) — only the
      // size (h-8/px-3/text-xs) stays this button's own, smaller, inline scale.
      fullClass:
        "tw-h-8 tw-rounded-2xl tw-bg-color-action-primary tw-px-3 tw-text-xs tw-font-semibold tw-text-color-text-on-action tw-shadow-elevation-card",
      ghostShadow: true,
      onClick: () => void this.confirmMember(member, row),
    });
  }

  private async resendInvite(row: MemberRow) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      await this.organizationUserService.postOrganizationUserReinvite(this.org.id, row.id);
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast("success", "", this.i18nService.t("inviteResent"));
      // The invite is re-dated, so its "Invited by you · …" time starts again.
      await this.loadMembers();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  // Withdraws a pending member from this team only. Their org invite can cover several teams
  // (see inviteToTeam), so while other teams remain just this one is dropped from it. Once it's
  // the last one, the OrganizationUser row is hard-deleted rather than soft-revoked: a soft
  // revoke leaves the email occupying a row server-side, which rejects a fresh invite to that
  // same address until it's restored. Deleting frees the email immediately so "+ Add member"
  // can reinvite it (and, for an Accepted member, is what tells them they were rejected).
  private async revokeMember(row: MemberRow, kind: "revoked" | "rejected") {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    const orgId = this.org.id;
    try {
      const details = await this.organizationUserService.getOrganizationUser(orgId, row.id);
      if (this.destroyed) {
        return;
      }
      const otherTeams = details.collections.filter((c) => c.id !== this.collectionId);
      if (!details.accessAll && otherTeams.length > 0) {
        await this.putMemberTeams(orgId, row.id, details, otherTeams);
      } else {
        await this.organizationUserService.deleteOrganizationUser(orgId, row.id);
      }
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast(
        "success",
        "",
        this.i18nService.t(kind === "rejected" ? "memberRejected" : "inviteCancelled"),
      );
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }

  private async removeMember(row: MemberRow) {
    if (row.isBusy) {
      return;
    }
    row.isBusy = true;
    try {
      // The server drops just this team, takes them out of the org too if it was their last
      // (a member of no team would be invisible in every team's list and stuck in someone else's
      // org), and emails them.
      await this.teamInvitationService.removeTeamMember(
        this.org.id,
        this.collectionId,
        row.id,
        this.originalTeamName,
      );
      if (this.destroyed) {
        return;
      }
      this.platformUtilsService.showToast(
        "success",
        "",
        this.i18nService.t("removedUserId", row.name || row.email),
      );
      await this.loadMembers();
      this.onMembersChanged?.();
    } catch (e) {
      if (this.destroyed) {
        return;
      }
      this.logService.error(e instanceof Error ? e.message : String(e));
      this.validationService.showError(e);
    } finally {
      if (!this.destroyed) {
        row.isBusy = false;
      }
    }
  }
}
